import { useEffect, useRef, type RefObject } from 'react'

// Never one with a negative tabindex, such as the input behind an MUI Select, which Tab skips.
const focusable = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
]
  .map((selector) => `${selector}:not([tabindex^="-"])`)
  .join(', ')

/**
 * What every popover the mockups open needs from the keyboard (`lib/useModalA11y.js`): on open,
 * focus moves inside, to the element marked `data-autofocus`, else the first control; Escape
 * closes it, but only while focus is inside it, so an open select menu takes Escape first; Tab
 * cycles inside it, unless `trap` is false; and on close focus returns to whatever opened it,
 * unless `restoreFocus` is false because the caller places focus itself.
 */
export function usePopoverA11y(
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  { trap = true, restoreFocus = true }: { readonly trap?: boolean; readonly restoreFocus?: boolean } = {},
): void {
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) {
      return undefined
    }
    const previous = document.activeElement
    const node = ref.current
    if (node !== null) {
      const target = node.querySelector<HTMLElement>('[data-autofocus]') ?? node.querySelector<HTMLElement>(focusable)
      ;(target ?? node).focus({ preventScroll: true })
    }

    function onKeyDown(event: KeyboardEvent) {
      const root = ref.current
      const active = document.activeElement
      if (root === null || active === null || !root.contains(active)) {
        return
      }
      // A dialog nested inside, such as a date picker, handles its own keys first.
      const dialog = active.closest('dialog, [role="dialog"]')
      if (dialog !== null && dialog !== root && root.contains(dialog)) {
        return
      }
      if (event.key === 'Escape') {
        event.stopPropagation()
        close.current()
        return
      }
      if (event.key !== 'Tab' || !trap) {
        return
      }
      const items = [...root.querySelectorAll<HTMLElement>(focusable)]
      const first = items[0]
      const last = items.at(-1)
      if (first === undefined || last === undefined) {
        event.preventDefault()
        return
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (restoreFocus && previous instanceof HTMLElement && document.contains(previous)) {
        previous.focus({ preventScroll: true })
      }
    }
  }, [open, ref, trap, restoreFocus])
}
